import React, { ReactElement } from 'react';
import classNames from 'classnames/bind';
import Link from 'next/link';
import styles from 'sections/DigitalTools/ShipSchedules/components/SectionFeatures/SectionFeatures.module.scss';

const cn = classNames.bind(styles);

const SectionFeatures = (): ReactElement => {
  return (
    <section className={cn('features')}>
      <div className={cn('container')}>

        <div className={cn('features__section')}>
          <div className={cn('features__image-box')}>
            <picture>
              <source
                srcSet="
                /images/pages/DigitalTools/ShipSchedules/localization-mobile.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/localization-mobile@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 575px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/ShipSchedules/localization-tablet.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/localization-tablet@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 1024px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/ShipSchedules/localization-desktop.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/localization-desktop@2x.webp 2x
              "
                sizes="100vw"
              />
              <img
                className={cn('features__image')}
                src="/images/pages/DigitalTools/ShipSchedules/localization-desktop.webp"
                alt="ship schedules"
              />
            </picture>
          </div>
          <div className={cn('features__content')}>
            <h2 className={cn('features__heading')}>
              Language Localisation
            </h2>
            <p className={cn('features__paragraph')}>
              We recognise that logistics is as local as it is global.
              That’s why our smart, streamlined systems are customised to support
              specific language settings, so your customers can communicate and
              collaborate confidently.
            </p>
            <div className={cn('features__buttons')}>
              <Link
                className={cn('features__button-request')}
                href="https://www.searates.com/shipping/request"
              >
                Request Your Quote
                <svg viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M1 5h7" />
                  <path d="M2 1l4 4-4 4" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        <div className={cn('features__section')}>
          <div className={cn('features__content')}>
            <h2 className={cn('features__heading')}>
              Web-integration of international shipping schedules
            </h2>
            <p className={cn('features__paragraph')}>
              Looking to better serve global shippers while delivering a streamlined
              and sophisticated digital experience? Seamlessly sync our vessel route
              schedule tracking into your website, so your customers can search,
              select, and stay informed on shipping routes without switching platforms.
            </p>
            <p className={cn('features__paragraph')}>
              Whether you're a scaling shipping service or a logistics leader, this powerful integration helps you:
            </p>
            <ul className={cn('features__list')}>
              <li className={cn('features__list-item')}>Cut carrier confusion and costly delays</li>
              <li className={cn('features__list-item')}>Provide precise, real-time route visibility</li>
              <li className={cn('features__list-item')}>Strengthen service standards and satisfaction</li>
            </ul>
          </div>

          <div className={cn('features__image-box')}>
            <picture>
              <source
                srcSet="
                /images/pages/DigitalTools/ShipSchedules/integration-mobile.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/integration-mobile@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 575px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/ShipSchedules/integration-tablet.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/integration-tablet@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 1024px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/ShipSchedules/integration-desktop.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/integration-desktop@2x.webp 2x
              "
                sizes="100vw"
              />
              <img
                className={cn('features__image')}
                src="/images/pages/DigitalTools/ShipSchedules/integration-desktop.webp"
                alt="ship schedules"
              />
            </picture>
          </div>
        </div>

        <div className={cn('features__section')}>
          <div className={cn('features__image-box')}>
            <picture>
              <source
                srcSet="
                /images/pages/DigitalTools/ShipSchedules/search-mobile.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/search-mobile@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 575px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/ShipSchedules/search-tablet.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/search-tablet@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 1024px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/ShipSchedules/search-desktop.webp 1x,
                /images/pages/DigitalTools/ShipSchedules/search-desktop@2x.webp 2x
              "
                sizes="100vw"
              />
              <img
                className={cn('features__image')}
                src="/images/pages/DigitalTools/ShipSchedules/search-desktop.webp"
                alt="ship schedules"
              />
            </picture>
          </div>
          <div className={cn('features__content')}>
            <h2 className={cn('features__heading')}>
              Get Started in Minutes
            </h2>
            <p className={cn('features__paragraph')}>
              No complicated onboarding. No concealed costs. Just clear, connected,
              and smarter shipping solutions. In just a few simple steps, you can
              start accessing accurate port schedules and providing prompt delivery
              timelines to your clients.
            </p>
            <p className={cn('features__paragraph')}>
              Search and scan international sailing schedules and shipment voyage specifics seamlessly.
            </p>
            <ul className={cn('features__list')}>
              <li className={cn('features__list-item')}>Boost business by bettering fulfillment flows and streamlining the supply chain.</li>
              <li className={cn('features__list-item')}>Feel free to connect with our team to power your logistics and trade through smart tech solutions.</li>
            </ul>
            <p className={cn('features__paragraph')}>
              This tool is included as part of the DFA Premium membership, offering
              exclusive perks and enhanced capabilities.
            </p>
            <p className={cn('features__paragraph')}>
              Visit our <Link className={cn('features__text-link')} href="/membership">Membership</Link> page to explore the full package.
            </p>
            <div className={cn('features__buttons')}>
              <Link
                className={cn('features__button-contact')}
                href="/company/contact-us"
              >
                Contact Our Team Today
              </Link>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default SectionFeatures;